import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const DURATIONS = [1, 2, 3, 4, 5];
const PATTERNS = ["Semester", "Yearly"];
const MODES = ["Regular", "Part-time", "Distance", "Online"];

const empty = {
  course_type: "",
  duration_years: 4,
  pattern: "Semester",
  mode: "Regular",
  is_active: true,
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export default function Courses() {
  const { role } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const { data } = await api.get("/institute/courses");
    setItems(data);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.course_type.trim()) {
      toast.error("Course Type is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        course_type: form.course_type.trim(),
        duration_years: Number(form.duration_years),
        pattern: form.pattern,
        mode: form.mode,
        is_active: form.is_active,
      };
      if (editing) {
        const { data } = await api.put(`/institute/courses/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Course updated");
      } else {
        const { data } = await api.post(`/institute/courses`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Course added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const c = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/courses/${c.id}`);
      setItems((arr) => arr.filter((x) => x.id !== c.id));
      toast.success("Course removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="courses-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Master</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Courses Offered</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Define the courses your institute offers — type, duration, pattern and mode. These
            are used when adding departments.
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!writable}
          data-testid="course-add-button"
          title={!writable ? "Only Institute Admin can add courses" : undefined}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} strokeWidth={1.5} /> Add Course
        </button>
      </div>

      {!writable && (
        <div
          data-testid="courses-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access. Only an Institute Admin can manage courses.
        </div>
      )}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          data-testid="courses-empty"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
        >
          <BookOpen size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No courses yet</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Add your first course to start tagging departments.
          </div>
          {writable && (
            <button
              onClick={openCreate}
              className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
            >
              <Plus size={14} strokeWidth={1.5} /> Add the first course
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="courses-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Course Type", "Duration", "Pattern", "Mode", "Status", ""].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`course-row-${c.id}`}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                >
                  <td className="px-4 py-4 text-[#1c1a17]">{c.course_type}</td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    {c.duration_years} {c.duration_years === 1 ? "Year" : "Years"}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{c.pattern}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{c.mode}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-block w-2 h-2 rounded-full mr-2 ${
                        c.is_active ? "bg-[#3a5a40]" : "bg-[#9e2a2b]"
                      }`}
                    />
                    <span className="text-xs text-[#6b655b]">
                      {c.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`course-edit-${c.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> {writable ? "Edit" : "View"}
                    </button>
                    {writable && (
                      <button
                        onClick={() => setConfirmDelete(c)}
                        data-testid={`course-delete-${c.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl"
          data-testid="course-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Course" : "Add Course"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Course Type *" full>
              <input
                data-testid="course-type-input"
                className={inputCls}
                value={form.course_type}
                disabled={!writable}
                placeholder="e.g., B.Tech in Computer Science"
                onChange={(e) => set("course_type", e.target.value)}
              />
            </Field>
            <Field label="Duration *">
              <select
                data-testid="course-duration-select"
                className={inputCls}
                value={form.duration_years}
                disabled={!writable}
                onChange={(e) => set("duration_years", Number(e.target.value))}
              >
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} {d === 1 ? "Year" : "Years"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Semester / Year Pattern *">
              <select
                data-testid="course-pattern-select"
                className={inputCls}
                value={form.pattern}
                disabled={!writable}
                onChange={(e) => set("pattern", e.target.value)}
              >
                {PATTERNS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Mode *" full>
              <select
                data-testid="course-mode-select"
                className={inputCls}
                value={form.mode}
                disabled={!writable}
                onChange={(e) => set("mode", e.target.value)}
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status" full>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="course-active-checkbox"
                  checked={form.is_active}
                  disabled={!writable}
                  onChange={(e) => set("is_active", e.target.checked)}
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">Course is active</span>
              </label>
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="course-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            {writable && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="course-save-button"
                className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this course?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.course_type} will be removed. Departments referencing it must be
              reassigned first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="course-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
